import { defineStore } from 'pinia'
import { computed } from 'vue'
import type { Member } from '../core/models/member/member'
import useGet from '../core/composables/api/useGet'
import { useEventBus } from '@vueuse/core'
import { EventKey } from '../core/events/event-key'

type MemberWithRole = Member & {
  isCoachee: boolean
  canReceiveFeedback: boolean
  canGiveFeedback: boolean
}

export const useMembersStore = defineStore('members', () => {
  const {
    data: members,
    load: fetchMembers,
    reload: refetchMembers,
  } = useGet<MemberWithRole[]>('members')

  const membersMap = computed(() => {
    const map = new Map<string, MemberWithRole>()
    if (members.value) {
      members.value.forEach((m) => map.set(m.id, m))
    }
    return map
  })

  const allCoachees = computed(() => {
    return members.value?.filter((member) => member.isCoachee) ?? []
  })

  const getMemberById = (id: string): MemberWithRole | null => {
    return membersMap.value.get(id) ?? null
  }

  const getMembersByIds = (id: string[]) => {
    return members.value.filter((member) => id.includes(member.id))
  }

  const getMemberByIdWithFallback = (id: string): MemberWithRole => {
    const member = getMemberById(id)
    if (member) return member

    return {
      id: 'unknown',
      email: '',
      canLogin: true,
      pending: false,
      firstName: 'Unknown',
      lastName: 'User',
      isCoachee: false,
      canReceiveFeedback: false,
      canGiveFeedback: false,
    }
  }

  const initialize = async () => {
    await fetchMembers()
    setupEventListeners()
  }

  const setupEventListeners = () => {
    const busKeys = [
      EventKey.organizationMemberCreated,
      EventKey.organizationMemberUpdated,
      EventKey.organizationMemberDeleted,
      EventKey.coachingDeleted,
      EventKey.coachingsAdded,
      EventKey.coachingAdded,
    ]

    busKeys.forEach((key) => {
      const bus = useEventBus(key)
      bus.on(async () => {
        await refetchMembers()
      })
    })
  }

  return {
    members,
    membersMap,
    allCoachees,
    getMemberById,
    getMembersByIds,
    getMemberByIdWithFallback,
    initialize,
    reload: refetchMembers,
  }
})
